import { useRef, useState } from 'react'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { SubprojectsPanelDocument } from 'generated/resources/graphql'
import { orderBy } from 'lodash'

import { observer } from 'mobx-react'

import SingleChoiceFilter from 'modules/core/components/Filter/filters/SingleChoice'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { Amount } from 'modules/project_management/components/Amount'
import { LoadingState } from 'modules/project_management/components/LoadingState'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'
import { ISubprojectsPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/types'

import EmployeeCard from './EmployeeCard'

export interface ISubprojectsSidepanelProps {
  onClose: () => void
  showSubprojectsPanel: boolean
  projectId: number
  projectCurrency: string
  canSeeLaborCosts: boolean
  subprojectPanelData: ISubprojectsPanelData
}

type SortOptionValues = 'alphabetical' | 'worked_hours' | 'labor_cost'

const SubprojectsSidepanel = ({
  onClose,
  showSubprojectsPanel,
  projectId,
  projectCurrency,
  canSeeLaborCosts,
  subprojectPanelData,
}: ISubprojectsSidepanelProps) => {
  const [sortOption, setSortOption] = useState<SortOptionValues | null>(null)
  const isSortByOpen = useRef(false)
  const { inputedMinutes, laborCostCents, subprojectId, subprojectName } = subprojectPanelData
  const { data, loading: loadingProjectWorkers } = useQuery(SubprojectsPanelDocument, {
    variables: {
      includeCost: canSeeLaborCosts,
      projectIds: [projectId],
      subprojectIds: subprojectId === 0 ? [] : [subprojectId],
      noSubproject: subprojectId === 0,
    },
  })

  const handleSortByToggle = (value: boolean) => {
    isSortByOpen.current = value
  }

  const projectWorkers = data?.projectManagement.projectWorkers || []

  const filteredProjectWorkers = projectWorkers.filter(
    (project_worker) => (project_worker.inputedMinutes ?? 0) > 0
  )

  const sortedProjectWorkers = () => {
    if (sortOption === 'worked_hours') {
      return orderBy(filteredProjectWorkers, 'inputedMinutes', 'desc')
    } else if (sortOption === 'labor_cost' && canSeeLaborCosts) {
      return orderBy(
        filteredProjectWorkers,
        (worker) => {
          return worker.laborCostCents || 0
        },
        'desc'
      )
    }

    return orderBy(filteredProjectWorkers, 'employee.fullName')
  }

  const sortByOptions = () => {
    const options: { value: SortOptionValues; label: string }[] = [
      {
        value: 'alphabetical',
        label: i18n.t('project_management.subprojects.details.sort_by.options.alphabetical'),
      },
      {
        value: 'worked_hours',
        label: i18n.t('project_management.subprojects.details.sort_by.options.worked_hours'),
      },
    ]

    if (canSeeLaborCosts) {
      options.push({
        value: 'labor_cost',
        label: i18n.t('project_management.subprojects.details.sort_by.options.labor_cost'),
      })
    }

    return options
  }

  return (
    <Sidepanel
      title={i18n.t('project_management.subprojects.details.title')}
      open={showSubprojectsPanel}
      onClose={onClose}
      closeLabel={i18n.t('common.close')}
    >
      <Text size="400" weight="medium">
        {subprojectName}
      </Text>
      <Box flexDirection="row" gap="s64">
        <Box paddingTop="s16" paddingBottom="s32">
          <Text size="200">{i18n.t('project_management.subprojects.details.total_hours')}</Text>
          <Text color="grey1000" size="400" weight="medium">
            {formatProjectHours(inputedMinutes)}
          </Text>
        </Box>
        {canSeeLaborCosts && (
          <Box paddingTop="s16" paddingBottom="s32">
            <Text size="200">{i18n.t('project_management.subprojects.details.cost')}</Text>
            <Amount
              amount={laborCostCents}
              currency={projectCurrency}
              size="400"
              weight="medium"
              color="grey1000"
            />
          </Box>
        )}
      </Box>
      <Divider />
      {loadingProjectWorkers ? (
        <LoadingState />
      ) : (
        <Box paddingTop="s32" gap="s16">
          <Text size="400" weight="medium">
            {i18n.t('project_management.subprojects.details.hours_per_employee')}
          </Text>
          <Box flexDirection="row" alignItems="center" justifyContent="flexStart" gap="s8">
            <SingleChoiceFilter
              label={i18n.t('project_management.subprojects.details.sort_by.label')}
              options={sortByOptions()}
              value={sortOption}
              onValueChange={setSortOption}
              onToggle={handleSortByToggle}
              onClose={() => handleSortByToggle(false)}
              noFooter
            />
          </Box>
          {sortedProjectWorkers().map(
            (project_worker) =>
              project_worker.employee && (
                <EmployeeCard
                  key={project_worker.id}
                  employee={project_worker.employee}
                  imputedMinutes={project_worker.inputedMinutes || 0}
                  laborCostCents={project_worker.laborCostCents}
                  laborCostCurrency={projectCurrency}
                  canSeeLaborCosts={canSeeLaborCosts}
                />
              )
          )}
        </Box>
      )}
    </Sidepanel>
  )
}

export default observer(SubprojectsSidepanel)
